import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useDragAndReorder } from 'hooks/useDragAndReorder';
import useChatChannels from 'hooks/useChatChannels';
import { useMonitoringChatQueues } from 'hooks/useMonitoringChatQueues';
import { useMonitoringChatQueuesSocket } from 'hooks/useMonitoringChatQueuesSocket';
import { usePauses } from 'hooks/usePauses';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import useQueues from 'hooks/useQueues';
import { useGeneralQueueConfig } from 'hooks/useGeneralQueueConfig';
import { useServiceLevels } from 'hooks/useServiceLevels';
import {
  loadObjectFromLocalStorage,
  saveObjectToLocalStorage,
  LOCAL_STORAGE_KEYS
} from 'utils/fuctions/localStorage';

import { MonitoringStatCard } from 'components/MonitoringStat/MonitoringStatCard';
import { MonitoringAgentQueueCard } from 'components/MonitoringAgentQueueCard';
import { AgentItem } from './components/AgentItem';
import { StatsSidebar } from './components/StatsSidebar';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { MonitoringHeader } from './components/MonitoringHeader';

import { statusColors } from 'utils/variables/statusColors';
import * as S from './styles';
import {
  getProcessedMonitoringData,
  layoutToMonitoringChatQueuesSocketGroup,
  getAgentsStatusCounts,
  getCardStatsConfig,
  getCircleStatsConfig,
  initialMatrixColumnOrder,
  getFilteredQueueMembers
} from './logic';

function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = loadObjectFromLocalStorage(key);
      return stickyValue !== null && stickyValue !== undefined
        ? stickyValue
        : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  });
  useEffect(() => {
    saveObjectToLocalStorage(key, value);
  }, [key, value]);
  return [value, setValue];
}

export const MonitoringScreenChat = () => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { config: generalConfig } = useGeneralQueueConfig();

  const [search, setSearch] = useState({ isActive: false, value: '' });

  const [activeFilters, setActiveFilters] = useState([]);

  const [matrixSorting, setMatrixSorting] = useState([]);
  const [matrixColumnOrder, setMatrixColumnOrder] = useState(
    initialMatrixColumnOrder
  );
  const [matrixColumnVisibility, setMatrixColumnVisibility] = useState({});

  const [sortOption, setSortOption] = useStickyState(
    'agent',
    LOCAL_STORAGE_KEYS.MONITORING_CHAT_SORT_OPTION
  );
  const [layoutOption, setLayoutOption] = useStickyState(
    'one_card_per_agent',
    LOCAL_STORAGE_KEYS.MONITORING_CHAT_LAYOUT_OPTION
  );
  const [cardOrder, setCardOrder] = useState([
    'TMA',
    'TMO',
    'MTW',
    'TME',
    'TDRD',
    'TDR',
    'TM_ASSIGN',
    'CHATS_IN_WAITING',
    'ATTENDANCE_LEVEL',
    'SERVICE_LEVEL',
    'SERVICE_LEVEL_PERCENTAGE'
  ]);

  const [selectedGroup, setSelectedGroup] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_CHAT_SELECTED_GROUP
  );
  const [manualSelection, setManualSelection] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_CHAT_MANUAL_SELECTION
  );

  const [selectedChatChannels, setSelectedChatChannels] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_CHAT_SELECTED_CHANNELS
  );

  const { options: allChatChannels } = useChatChannels();

  const { response: allQueuesList } = useQueues({
    types: ['is_chat'],
    showDisabled: false,
    shouldUseOnlyAllowedUserQueues: true
  });
  const { response: savedGroups, fetch: fetchGroups } =
    useMonitoringQueueGroups({ itemsPerPage: 100 });
  const { response: pauses } = usePauses();

  useEffect(() => {
    if (selectedChatChannels && !Array.isArray(selectedChatChannels)) {
      setSelectedChatChannels([]);
    }
  }, [selectedChatChannels, setSelectedChatChannels]);

  useEffect(() => {
    if (
      allChatChannels &&
      allChatChannels.length > 0 &&
      selectedChatChannels === null
    ) {
      setSelectedChatChannels(allChatChannels.map((c) => String(c.value)));
    }
  }, [allChatChannels, selectedChatChannels, setSelectedChatChannels]);

  useEffect(() => {
    if (selectedGroup && savedGroups.length > 0) {
      const updatedGroup = savedGroups.find((g) => g.id === selectedGroup.id);
      if (updatedGroup) {
        const getSig = (g) =>
          (g.queues || [])
            .map((q) => q.id)
            .sort((a, b) => a - b)
            .join(',');
        if (
          getSig(selectedGroup) !== getSig(updatedGroup) ||
          selectedGroup.name !== updatedGroup.name
        ) {
          setSelectedGroup(updatedGroup);
        }
      } else {
        setSelectedGroup(null);
      }
    }
  }, [savedGroups, selectedGroup, setSelectedGroup]);

  useEffect(() => {
    if (
      !selectedGroup &&
      manualSelection === null &&
      allQueuesList.length > 0
    ) {
      setManualSelection(allQueuesList);
    }
  }, [selectedGroup, manualSelection, allQueuesList, setManualSelection]);

  const activeQueueNames = useMemo(() => {
    if (selectedGroup && selectedGroup.queues)
      return selectedGroup.queues.map((q) => q.name);
    if (manualSelection && manualSelection.length > 0)
      return manualSelection.map((q) => q.name);
    return [];
  }, [selectedGroup, manualSelection]);

  const activeAgentLogins = useMemo(() => {
    if (
      selectedGroup &&
      selectedGroup.agents &&
      selectedGroup.agents.length > 0
    )
      return selectedGroup.agents.map((a) => a.login);
    return [];
  }, [selectedGroup]);

  const activeQueuesSignature = useMemo(
    () => JSON.stringify(activeQueueNames.sort()),
    [activeQueueNames]
  );

  const activeChannelsSignature = useMemo(
    () => JSON.stringify((selectedChatChannels || []).sort()),
    [selectedChatChannels]
  );

  const { response: monitoringData } = useMonitoringChatQueues({
    queueNames: activeQueueNames,
    channels: selectedChatChannels,
    agents: selectedGroup?.agents || [],
    autoRefresh: true,
    refreshInterval: 30000
  });

  const [queuesLoginResponse, setQueuesLoginResponse] = useState({
    successfull: [],
    rejected: []
  });
  const postJoinEmptyQueuesRef = useRef(false);

  const { response: monitoringSocketResponse } = useMonitoringChatQueuesSocket({
    queueNames: activeQueueNames,
    channels: selectedChatChannels,
    group: layoutToMonitoringChatQueuesSocketGroup(layoutOption),
    agents: selectedGroup?.agents || [],
    onJoined: (res) => {
      postJoinEmptyQueuesRef.current = !res?.successfull?.length;
      setQueuesLoginResponse({
        successfull: res?.successfull || [],
        rejected: res?.rejected || []
      });
    }
  });

  useEffect(() => {
    if (
      !activeQueueNames.length &&
      !(selectedChatChannels || []).length &&
      postJoinEmptyQueuesRef.current
    )
      toast.warn('Você não está monitorando nenhuma fila ou canal', {
        autoClose: 3000
      });

    if (queuesLoginResponse?.rejected?.length)
      toast.warn(
        `Não foi possível logar: ${queuesLoginResponse.rejected.join(', ')}`,
        { autoClose: 3000 }
      );
  }, [queuesLoginResponse]);

  useEffect(() => {
    const reloadTimeMinutes = Number(generalConfig?.monitoringReloadTime);
    if (!reloadTimeMinutes || reloadTimeMinutes <= 0) return;
    const timer = setTimeout(
      () => window.location.reload(),
      reloadTimeMinutes * 60 * 1000
    );
    return () => clearTimeout(timer);
  }, [generalConfig?.monitoringReloadTime]);

  const { groupedByType: serviceLevelsByType } = useServiceLevels({
    targets: ['MONITORING_QUEUE_CHAT']
  });
  const chatServiceLevelConfig =
    serviceLevelsByType?.MONITORING_QUEUE_CHAT?.[0];

  const cardStats = useMemo(
    () => getCardStatsConfig(monitoringData, cardOrder, chatServiceLevelConfig),
    [monitoringData, cardOrder, chatServiceLevelConfig]
  );

  const formattedAgents = useMemo(() => {
    return getProcessedMonitoringData(
      monitoringSocketResponse,
      layoutToMonitoringChatQueuesSocketGroup(layoutOption),
      activeQueueNames,
      pauses,
      activeAgentLogins
    );
  }, [
    monitoringSocketResponse,
    layoutOption,
    pauses,
    activeQueueNames,
    activeAgentLogins
  ]);

  const agentsStatusCounts = useMemo(
    () => getAgentsStatusCounts(formattedAgents),
    [formattedAgents]
  );
  const circleStats = useMemo(
    () =>
      getCircleStatsConfig(monitoringData, agentsStatusCounts, activeFilters),
    [monitoringData, agentsStatusCounts, activeFilters]
  );

  const toggleSearch = useCallback(() => {
    setSearch((prev) => {
      const ns = {
        isActive: !prev.isActive,
        value: !prev.isActive ? prev.value : ''
      };
      localStorage.setItem('searchState', JSON.stringify(ns));
      return ns;
    });
  }, []);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearch((prev) => {
      const ns = { ...prev, value: val };
      localStorage.setItem('searchState', JSON.stringify(ns));
      return ns;
    });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('searchState');
    if (saved)
      try {
        setSearch(JSON.parse(saved));
      } catch (e) {}
  }, []);

  const handleCircleClick = (prop) => {
    if (!prop.clickable) return;
    const key = prop.filterKey;

    setActiveFilters((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const filterAgents = useCallback(
    (data, searchTerm) => {
      let result = data;

      if (searchTerm && searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase().trim();
        const isQueueMode =
          layoutToMonitoringChatQueuesSocketGroup(layoutOption) === 'queue';

        if (isQueueMode) {
          result = result.filter(
            (q) =>
              q.name?.toLowerCase().includes(term) ||
              q.agents?.some(
                (a) =>
                  a.name?.toLowerCase().includes(term) ||
                  a.login?.toLowerCase().includes(term)
              )
          );
        } else {
          result = result.filter(
            (a) =>
              a.agentName?.toLowerCase().includes(term) ||
              a.agentLogin?.toLowerCase().includes(term) ||
              a.queueName?.toLowerCase().includes(term)
          );
        }
      }

      return getFilteredQueueMembers(result, activeFilters);
    },
    [layoutOption, activeFilters]
  );

  const filteredAgents = useMemo(() => {
    return filterAgents(formattedAgents || [], search.value);
  }, [
    formattedAgents,
    search.value,
    filterAgents,
    activeFilters,
    layoutOption
  ]);

  const {
    handleDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDragEnd
  } = useDragAndReorder(cardOrder, setCardOrder);

  const sortedAgents = useMemo(() => {
    if (!filteredAgents.length) return filteredAgents;

    return [...filteredAgents].sort((a, b) => {
      switch (sortOption) {
        case 'queue':
          if (layoutOption === 'one_card_per_queue') {
            return (a.name || '').localeCompare(b.name || '');
          }
          if (layoutOption === 'one_card_per_agent_per_queue') {
            return (a.queueName || '').localeCompare(b.queueName || '');
          }
          if (layoutOption === 'one_card_per_agent') {
            const aFirstQueue = a.queues?.[0]?.name || '';
            const bFirstQueue = b.queues?.[0]?.name || '';
            return aFirstQueue.localeCompare(bFirstQueue);
          }
          return 0;

        case 'agent':
        default: {
          if (layoutOption === 'one_card_per_queue') {
            return 0;
          }
          const aName = a.agentName?.name ?? a.agentName ?? a.name ?? '';
          const bName = b.agentName?.name ?? b.agentName ?? b.name ?? '';
          return aName.localeCompare(bName);
        }
      }
    });
  }, [filteredAgents, sortOption, layoutOption]);

  const renderAgents = () =>
    sortedAgents.map((agent) => (
      <AgentItem
        key={`${agent.agentLogin}-${agent.queueName}`}
        agent={agent}
        layoutOption={layoutOption}
        borderRadius={systemConfiguration.border_radius}
      />
    ));
  const renderQueueMode = () => (
    <S.QueueMetricsContainer>
      {filteredAgents.map((queue) => {
        let bg = statusColors.OFFLINE;
        if (queue.agents?.length) {
          if (queue.agents.some((a) => a.status === 'isAvailable')) bg = '#fff';
          else if (
            queue.agents.some((a) =>
              ['isPaused', 'isActivePause'].includes(a.status)
            )
          )
            bg = statusColors.PAUSE;
        }
        return (
          <S.QueueCard
            key={queue.id}
            borderRadius={systemConfiguration.border_radius}
            backgroundColor={bg}
          >
            <S.QueueTitle
              borderRadius={systemConfiguration.border_radius}
              color={systemConfiguration.secondary_color}
            >
              {queue.name}
            </S.QueueTitle>
            <S.QueueStatsGrid>
              {[
                {
                  id: 'TMA',
                  title: 'T.M.A',
                  value: (q) => q.TMA || '00:00:00'
                },
                {
                  id: 'TME',
                  title: 'T.M.E',
                  value: (q) => q.TME || '00:00:00'
                },
                {
                  id: 'in_waiting',
                  title: 'Fila',
                  value: (q) => q.chatInWaiting || 0
                },
                {
                  id: 'chatsInAttendance',
                  title: 'Atend.',
                  value: (q) => q.chatsInAttendance || 0
                }
              ].map((s) => (
                <MonitoringStatCard
                  key={s.id}
                  title={s.title}
                  value={
                    typeof s.value === 'function' ? s.value(queue) : s.value
                  }
                  layout="vertical-centered-single"
                />
              ))}
            </S.QueueStatsGrid>
            <S.QueueAgentsContainer>
              {queue.agents?.map((a) => (
                <MonitoringAgentQueueCard
                  key={a.login}
                  agent={a}
                  borderRadius={systemConfiguration.border_radius}
                  pauses={pauses}
                />
              ))}
            </S.QueueAgentsContainer>
          </S.QueueCard>
        );
      })}
    </S.QueueMetricsContainer>
  );

  return (
    <S.MainContainer>
      <S.TopSection
        isQueueLayout={
          layoutToMonitoringChatQueuesSocketGroup(layoutOption) !== 'queue'
        }
      >
        {layoutToMonitoringChatQueuesSocketGroup(layoutOption) !== 'queue' && (
          <StatsSidebar
            cardStats={cardStats}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            systemConfiguration={systemConfiguration}
          />
        )}

        <S.ContentContainer>
          <MonitoringHeader
            systemConfiguration={systemConfiguration}
            circleStats={circleStats}
            onCircleClick={handleCircleClick}
            activeFilters={activeFilters}
            statusFilter={null}
            groups={savedGroups}
            selectedGroup={selectedGroup}
            onSelectGroup={(g) => {
              setSelectedGroup(g);
              setManualSelection([]);
            }}
            onClearGroup={() => {
              setSelectedGroup(null);
              setManualSelection([]);
            }}
            manualSelection={manualSelection}
            onManualChange={(q) => {
              setManualSelection(q);
              setSelectedGroup(null);
            }}
            allQueues={allQueuesList}
            onGroupsChanged={fetchGroups}
            searchState={search}
            onToggleSearch={toggleSearch}
            onSearchChange={handleSearchChange}
            queuesLoginResponse={queuesLoginResponse.successfull}
            sortOption={sortOption}
            setSortOption={setSortOption}
            layoutOption={layoutOption}
            setLayoutOption={setLayoutOption}
            allChatChannels={allChatChannels}
            selectedChatChannels={selectedChatChannels}
            setSelectedChatChannels={setSelectedChatChannels}
          />

          <S.MainContent>
            {filteredAgents.length === 0 &&
            (search.value || activeFilters.length > 0) ? (
              <S.NoResultsMessage>
                Nenhum resultado encontrado.
              </S.NoResultsMessage>
            ) : layoutToMonitoringChatQueuesSocketGroup(layoutOption) ===
              'queue' ? (
              renderQueueMode()
            ) : (
              <S.AgentsContainer>{renderAgents()}</S.AgentsContainer>
            )}
          </S.MainContent>
        </S.ContentContainer>
      </S.TopSection>

      <AnalyticsDashboard
        layoutOption={layoutOption}
        monitoringData={monitoringData}
        tableState={{
          sorting: matrixSorting,
          setSorting: setMatrixSorting,
          columnOrder: matrixColumnOrder,
          setColumnOrder: setMatrixColumnOrder,
          columnVisibility: matrixColumnVisibility,
          setColumnVisibility: setMatrixColumnVisibility
        }}
      />
    </S.MainContainer>
  );
};
